import { Schedule as ClockIcon, Edit as EditIcon } from "@mui/icons-material";
import { Box, IconButton, Stack, styled } from "@mui/material";
import {
  PickersLayoutContentWrapper,
  PickersLayoutProps,
  PickersLayoutRoot,
  TimeField,
  TimePickerToolbar,
  TimePickerToolbarProps,
  TimeView,
  pickersLayoutClasses,
  usePickerLayout,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import { localeMap } from "lang";
import { useState } from "react";
import { useIntl } from "react-intl";

const StyledMobileTimePicker = styled(MobileTimePicker)(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.palette.common.white,
  color: theme.palette.info.main,
  "& label.Mui-focused": {
    color: theme.palette.info.main,
  },
  "& label.Mui-error": {
    color: theme.palette.error.main,
  },
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": {
      borderColor: theme.palette.info.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.info.main,
    },
  },
  "& .MuiOutlinedInput-root.Mui-error": {
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.error.main,
    },
  },
}));

const StyledMobileTimePickerReadOnly = styled(MobileTimePicker)(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.palette.common.white,
  "& label.Mui-focused": {
    color: theme.palette.secondary.dark,
  },
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": {
      borderColor: theme.palette.secondary.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.secondary.main,
      borderWidth: 1,
    },
  },
  userSelect: "none",
  pointerEvents: "none",
}));

export interface AsolviTimePickerProps {
  onChange: (date: Date) => void | (() => void);
  label?: string;
  value: Date | null | undefined;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  name?: string;
  margin?: "none" | "dense" | "normal";
  placeholder?: string;
  error?: string;
  readOnly?: boolean;
  size?: "small" | "medium" | undefined;
  orientation?: "portrait" | "landscape";
}

export const AsolviTimePicker = (props: AsolviTimePickerProps) => {
  const intl = useIntl();

  const LayoutWithKeyboardView = (pickerProps: PickersLayoutProps<any, Date, TimeView>) => {
    const { value, onChange } = pickerProps;
    const [showKeyboardView, setShowKeyboardView] = useState(false);
    const intl = useIntl();

    const { toolbar, tabs, content, actionBar } = usePickerLayout({
      ...pickerProps,
      slotProps: {
        ...pickerProps.slotProps,
        toolbar: {
          ...pickerProps.slotProps?.toolbar,
          // @ts-ignore
          showKeyboardView,
          setShowKeyboardView,
        },
      },
    });

    return (
      <PickersLayoutRoot ownerState={pickerProps}>
        {toolbar}
        {actionBar}
        <PickersLayoutContentWrapper className={pickersLayoutClasses.contentWrapper}>
          {tabs}
          {showKeyboardView ? (
            <Box sx={{ mx: 3, my: 2, width: 272 }}>
              <TimeField
                value={value}
                onChange={onChange}
                sx={{ width: "100%" }}
                ampm={localeMap(intl.locale).ampm}
                data-testid="AsolviTimePicker-KeyboardTextField"
              />
            </Box>
          ) : (
            content
          )}
        </PickersLayoutContentWrapper>
      </PickersLayoutRoot>
    );
  };

  const ToolbarWithKeyboardViewSwitch = (
    props: TimePickerToolbarProps<any> & {
      showKeyboardView?: boolean;
      setShowKeyboardView?: React.Dispatch<React.SetStateAction<boolean>>;
    }
  ) => {
    const { showKeyboardView, setShowKeyboardView, ...other } = props;

    return (
      <Stack
        spacing={2}
        direction={other.isLandscape ? "column" : "row"}
        alignItems="center"
        sx={
          other.isLandscape
            ? {
                gridColumn: 1,
                gridRow: "1 / 3",
              }
            : { gridColumn: "1 / 4", gridRow: 1, mr: 1 }
        }
      >
        <TimePickerToolbar {...other} sx={{ flex: "1 1 100%" }} />
        <IconButton
          color="inherit"
          onClick={() => setShowKeyboardView!((prev) => !prev)}
          data-testid="AsolviTimePicker-KeyboardViewSwitch"
        >
          {showKeyboardView ? <ClockIcon /> : <EditIcon />}
        </IconButton>
      </Stack>
    );
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={localeMap(intl.locale).locale}
    >
      {props.disabled || props.readOnly ? (
        <StyledMobileTimePickerReadOnly
          label={props.label}
          value={props.value}
          ampm={localeMap(intl.locale).ampm}
        />
      ) : (
        <StyledMobileTimePicker
          localeText={{
            cancelButtonLabel: intl.formatMessage({
              id: "general.cancel",
              defaultMessage: "Cancel",
            }),
            todayButtonLabel: intl.formatMessage({
              id: "date.now",
              defaultMessage: "Now",
            }),
            okButtonLabel: intl.formatMessage({
              id: "general.ok",
              defaultMessage: "Ok",
            }),
          }}
          label={props.label}
          value={props.value}
          ampm={localeMap(intl.locale).ampm}
          orientation={props.orientation}
          onAccept={(time: any) => {
            props.onChange(time as Date);
          }}
          closeOnSelect={false}
          slots={{
            layout: LayoutWithKeyboardView,
            toolbar: ToolbarWithKeyboardViewSwitch,
          }}
          slotProps={{
            actionBar: {
              actions: ["today", "cancel", "accept"],
            },
            textField: {
              id: props.id,
              name: props.name,
              variant: "outlined",
              margin: props.margin,
              label: props.label,
              placeholder: props.placeholder,
              value: props.value,
              required: props.required,
              fullWidth: true,
              error: !!props.error,
              helperText: props.error,
              size: props.size,
            },
          }}
        />
      )}
    </LocalizationProvider>
  );
};
